import React, { useMemo, useState } from 'react';
import {
  Link as LinkIcon, Copy, Check, UserPlus, Mail, AlertCircle, CheckCircle2,
  ChevronRight, Wallet, Clock, BadgeCheck,
} from 'lucide-react';
import { notifyToast } from '../../lib/toastStore';
import { REFERRER_RESTRICTED_NOTICE } from '../../lib/orgApproval';
import {
  WORKFLOW_STAGES, STAGE_LABELS, resolveCandidateStage, daysSinceUpdate,
} from '../../lib/referrerStages';
import { summarizeCommissions } from '../../lib/referrerCommissions';

interface Props {
  candidates: any[];
  commissions: any[];
  invitations: any[];
  referralCode: string;
  referralLink: string;
  commissionsLabel: string;
  onNavigate: (tab: string) => void;
  onAddCandidate: () => void;
  onSendInvitation: () => void;
  /**
   * PR-13b affordance honesty: when the referrer's referring is restricted
   * (the same signal that drives the dashboard banner), every referring
   * action renders DISABLED with the restriction reason instead of looking
   * live and failing only on submit. The panel itself — link, code,
   * pipeline, payouts — stays fully visible read-only.
   */
  restricted?: boolean;
}

interface AttentionItem {
  id: string;
  text: string;
  tab: string;
}

/**
 * §2.1 Next-action home: the top of the referrer dashboard answers
 * "what needs me now?" — referral link + code with one-tap copy, the
 * primary Add Candidate CTA, the pipeline by the 8 workflow stages,
 * payouts from stamped commissions only, and a Needs attention list.
 * Rendered above the shared DashboardShell content.
 */
export default function ReferrerHomePanel({
  candidates, commissions, invitations, referralCode, referralLink,
  commissionsLabel, onNavigate, onAddCandidate, onSendInvitation,
  restricted = false,
}: Props) {
  const [copied, setCopied] = useState(false);

  const activeCandidates = useMemo(
    () => (candidates || []).filter((c: any) => c.status !== 'archived' && c.isArchived !== true),
    [candidates],
  );

  const pipeline = useMemo(() => {
    const counts = new Map<string, number>();
    activeCandidates.forEach((c: any) => {
      const key = resolveCandidateStage(c);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return WORKFLOW_STAGES.map((s) => ({ ...s, count: counts.get(s.key) || 0 }));
  }, [activeCandidates]);

  const payouts = useMemo(() => summarizeCommissions(commissions || []), [commissions]);

  const attention = useMemo<AttentionItem[]>(() => {
    const items: AttentionItem[] = [];
    const now = new Date();
    // Drafts going stale — the applicant hasn't submitted yet.
    activeCandidates
      .filter((c: any) => resolveCandidateStage(c) === 'draft')
      .map((c: any) => ({ c, days: daysSinceUpdate(c, now) ?? 0 }))
      .filter(({ days }) => days >= 7)
      .sort((a, b) => b.days - a.days)
      .slice(0, 3)
      .forEach(({ c, days }) => items.push({
        id: `draft-${c.id}`,
        text: `${c.firstName || 'Candidate'} ${c.lastName || ''} is still in Draft after ${days} days — a nudge may help them submit.`,
        tab: 'candidates',
      }));
    // In-progress candidates with no movement for two weeks.
    activeCandidates
      .filter((c: any) => { const k = resolveCandidateStage(c); return k !== 'draft' && k !== 'closed'; })
      .map((c: any) => ({ c, days: daysSinceUpdate(c, now) ?? 0 }))
      .filter(({ days }) => days >= 14)
      .sort((a, b) => b.days - a.days)
      .slice(0, 3)
      .forEach(({ c, days }) => items.push({
        id: `stalled-${c.id}`,
        text: `${c.firstName || 'Candidate'} ${c.lastName || ''} — no movement for ${days} days (${STAGE_LABELS[resolveCandidateStage(c)]}).`,
        tab: 'candidates',
      }));
    // Invitations sent a while ago, never opened or registered.
    (invitations || [])
      .filter((inv: any) => (inv.status || 'Sent') === 'Sent')
      .map((inv: any) => ({ inv, days: daysSinceUpdate({ updatedAt: inv.sentAt || inv.createdAt }, now) ?? 0 }))
      .filter(({ days }) => days >= 3)
      .slice(0, 2)
      .forEach(({ inv, days }) => items.push({
        id: `invite-${inv.id}`,
        text: `${inv.email} hasn't opened their invitation yet (${days} days) — consider resending it.`,
        tab: 'invitations',
      }));
    return items.slice(0, 5);
  }, [activeCandidates, invitations]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notifyToast('error', 'Could not copy the link — copy it manually instead.');
    }
  };

  const money = (n: number) => `$${n.toLocaleString()}`;

  return (
    <div className="space-y-4 mb-6">
      {/* (a) Referral link + code card and (b) primary CTAs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <LinkIcon className="h-3.5 w-3.5 text-brand" /> Your Referral Link
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {restricted
                  ? 'This link is paused while your referring is restricted — signups through it will not be linked to you.'
                  : 'Share it anywhere — anyone who signs up through it is linked to you automatically.'}
              </p>
            </div>
            <span className="px-3 py-1.5 bg-slate-100 rounded-lg font-mono text-xs font-bold text-slate-700 tracking-wider shrink-0">
              {referralCode}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <p className={`flex-1 min-w-0 truncate font-mono text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 ${restricted ? 'text-slate-400' : 'text-slate-600'}`}>
              {referralLink}
            </p>
            <button
              onClick={handleCopy}
              disabled={restricted}
              title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
              className="px-4 py-2.5 btn-primary text-white rounded-xl text-sm font-semibold transition flex items-center gap-1.5 shadow-lg shadow-blue-600/20 cursor-pointer shrink-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span>{copied ? 'Copied' : 'Copy Link'}</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5 flex flex-col justify-center gap-2">
          <button
            onClick={onAddCandidate}
            disabled={restricted}
            title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
            className="w-full px-4 py-3 btn-primary text-white rounded-xl text-sm font-semibold transition flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
          >
            <UserPlus className="h-4 w-4" /> Add Candidate
          </button>
          <button
            onClick={onSendInvitation}
            disabled={restricted}
            title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
            className="w-full px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-100"
          >
            <Mail className="h-4 w-4" /> Send Invitation
          </button>
        </div>
      </div>

      {/* (c) Pipeline strip — counts by the 8 workflow stages */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-4">
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-xs font-bold text-slate-700">Candidate Pipeline</p>
          <button onClick={() => onNavigate('candidates')} className="text-xs text-brand font-semibold hover:underline cursor-pointer inline-flex items-center gap-0.5">
            View candidates <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {pipeline.map((s) => (
            <button
              key={s.key}
              onClick={() => onNavigate('candidates')}
              className="rounded-xl bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-100 px-2 py-3 text-center transition cursor-pointer"
            >
              <p className="text-lg font-extrabold text-slate-900 leading-none">{s.count}</p>
              <p className="text-[10px] font-semibold text-slate-500 mt-1.5 leading-tight">{s.label}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* (d) Payouts — stamped commissions only */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Wallet className="h-3.5 w-3.5 text-brand" /> Payouts
            </p>
            <button onClick={() => onNavigate('commissions')} className="text-xs text-brand font-semibold hover:underline cursor-pointer inline-flex items-center gap-0.5">
              {commissionsLabel} <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="flex items-center gap-1 text-slate-500"><Clock className="h-3.5 w-3.5" /><p className="text-[10px] font-bold uppercase tracking-wide">Accruing</p></div>
              <p className="text-base font-extrabold text-slate-900 mt-1">{money(payouts.accruing)}</p>
              <p className="text-[11px] text-slate-500">{payouts.counts.accruing} in progress</p>
            </div>
            <div className="rounded-xl bg-blue-50 p-3">
              <div className="flex items-center gap-1 text-blue-500"><BadgeCheck className="h-3.5 w-3.5" /><p className="text-[10px] font-bold uppercase tracking-wide">Payable</p></div>
              <p className="text-base font-extrabold text-slate-900 mt-1">{money(payouts.payable)}</p>
              <p className="text-[11px] text-slate-500">{payouts.counts.payable} queued</p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3">
              <div className="flex items-center gap-1 text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /><p className="text-[10px] font-bold uppercase tracking-wide">Paid Out</p></div>
              <p className="text-base font-extrabold text-slate-900 mt-1">{money(payouts.paid)}</p>
              <p className="text-[11px] text-slate-500">{payouts.counts.paid} paid</p>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Amounts are the commission rates stamped when each candidate was referred — later rate changes never alter them.
          </p>
        </div>

        {/* (e) Needs attention */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5 mb-3">
            <AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Needs Attention
          </p>
          {attention.length === 0 ? (
            <div className="flex items-start gap-2.5 rounded-xl bg-emerald-50/70 border border-emerald-100 p-3.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-800 leading-relaxed">
                Nothing needs your attention right now. New stalls and unopened invitations will show up here.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {attention.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => onNavigate(item.tab)}
                    className="w-full text-left flex items-center gap-2.5 rounded-xl bg-amber-50/70 hover:bg-amber-50 border border-amber-100 px-3.5 py-2.5 transition cursor-pointer"
                  >
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="flex-1 text-xs text-amber-900 leading-relaxed">{item.text}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
